import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import store from '@/stores/store';
import { resolvePermissionCheck } from '@/utils/groupPermissions';
const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 20, ordering: '-creation_date' };

const createState = (options = {}) => ({
    receiptitems: [],
    receiptitems_url: '/api/receiptitems/',
    receiptitems_filters: DEFAULT_FILTERS,
    receiptitems_count: 0,
    receiptitems_loaded: false,
    receiptitems_loading: false,
    receiptitems_loading_errors: null,

    receiptitems_stats: {},
    receiptitems_stats_loaded: false,
    receiptitems_stats_loading: false,
    receiptitems_stats_loading_errors: null,
    cancelTokenSource: null,
    cancelTokenSourceStats: null,
    _storageOptions: options || {},
});

const mutations = {
    updateReceiptItems(state, receiptitems) {
        state.receiptitems = receiptitems;
        state.receiptitems_loaded = true;
        state.receiptitems_loading = false;
        state.receiptitems_loading_errors = null;
    },
    updateReceiptItemsUrl(state, receiptitems_url) {
        state.receiptitems_url = receiptitems_url;
    },
    updateReceiptItemsCount(state, receiptitems_count) {
        state.receiptitems_count = receiptitems_count;
    },
    updateReceiptItemsFilters(state, receiptitems_filters) {
        state.receiptitems_filters = receiptitems_filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.receiptitems_filters, fieldsToSave);
        }
    },
    updateReceiptItemsLoading(state, receiptitems_loading) {
        state.receiptitems_loading = receiptitems_loading;
    },
    updateReceiptItemsLoadingErrors(state, receiptitems_loading_errors) {
        state.receiptitems_loading_errors = receiptitems_loading_errors;
        state.receiptitems_loading = false;
    },

    updateReceiptItemsStats(state, receiptitems_stats) {
        state.receiptitems_stats = receiptitems_stats;
        state.receiptitems_stats_loaded = true;
    },
    updateReceiptItemsStatsLoading(state, loading) {
        state.receiptitems_stats_loading = loading;
    },
    updateReceiptItemsStatsLoadingErrors(state, errors) {
        state.receiptitems_stats_loading_errors = errors;
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    updateCancelTokenSourceStats(state, source) {
        state.cancelTokenSourceStats = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },

};

const actions = {

    async fetchReceiptItems({ commit, dispatch, state }, params) {
        if (!resolvePermissionCheck(store.getters['session/current_user_permissions'], "core.view_receiptitem")) {
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSource', source);

        commit('updateReceiptItemsLoading', true);
        commit('updateReceiptItemsLoadingErrors', null);
        try {
            const response = await axios.get(state.receiptitems_url, {
                params: state.receiptitems_filters,
                cancelToken: source.token,
            });
            commit('updateReceiptItemsCount', response.data.count);
            commit('updateReceiptItems', response.data.results);
            dispatch('fetchReceiptItemsStats');
            return response;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateReceiptItemsLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateReceiptItemsLoading', false);
        }
    },

    async fetchReceiptItemsStats({ commit, dispatch, state }, params) {
        if (store.getters['session/current_user_permissions'].indexOf("order.view_allocation") == -1) {
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSourceStats) {
            state.cancelTokenSourceStats.cancel("New stats fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSourceStats', source);

        commit('updateReceiptItemsStatsLoading', true);
        commit('updateReceiptItemsStatsLoadingErrors', null);

        try {
            const response = await axios.get('/api/receiptitems/stats/', {
                params: state.receiptitems_filters,
                cancelToken: source.token,
            });
            commit('updateReceiptItemsStatsLoading', false);
            commit('updateReceiptItemsStats', response.data);
            return response;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateReceiptItemsStatsLoadingErrors', error.details);
            commit('updateReceiptItemsStatsLoading', false);
            throw error;
        }
    },

    async init({ commit, dispatch, state }, params) {
        await commit('updateReceiptItems', []);
        
        // Update storage options if provided
        if (params?.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        
        // Load filters from localStorage if storage is configured
        await commit('updateReceiptItemsFilters', { 
            ...DEFAULT_FILTERS, 
            ...(state._storageOptions?.storageKey ? loadFiltersFromStorage(state._storageOptions?.storageKey, {}) : {}),
            ...(params?.filters || {})
        });
        
        if (params && params.url) {
            await commit('updateReceiptItemsUrl', params.url);
        }
    },
};


export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions
});